import { cn } from '@glade-ai/ui';
import { type FC, useMemo } from 'react';

import * as format from '@/format';
import { useTaskEfficiencyContext } from '@/providers/TaskEfficiency';
import type { TaskEfficiencyReferenceType } from '@/tsClient/servers/noodleApi/ApiDefinition/taskEfficiency';
import { useTaskEfficiency } from '@/tsClient/taskEfficiency';

type Props = {
  /** The type of building block (e.g., 'invoice', 'form_request') */
  referenceType: TaskEfficiencyReferenceType;
  /** The ID of the building block */
  referenceId: string;
  /** Whether the task is currently skipped */
  isSkipped?: boolean;
  /** Custom className for styling */
  className?: string;
};

type DisplayData = {
  label: string;
  milliseconds: number;
  formatted: string;
  suffix?: string;
};

/**
 * Displays task efficiency completion time for a building block.
 *
 * Display states:
 * - Not started: No indicator shown
 * - In progress: "In progress: 1d 2h" (elapsed since created)
 * - Completed: "Completed: 2d 4h 30m"
 * - Completed + Reopened: "Completed: 2d 4h 30m (reopened 1x)"
 * - Skipped: No indicator shown
 */
const TaskEfficiencyIndicator: FC<Props> = ({ referenceType, referenceId, isSkipped = false, className }) => {
  const batchContext = useTaskEfficiencyContext();

  // Use batch context if available, otherwise fall back to individual fetch
  const { data: individualEfficiency } = useTaskEfficiency(
    !batchContext && !isSkipped ? { referenceId, referenceType } : null,
  );

  const efficiency = batchContext ? batchContext.getEfficiency(referenceType, referenceId) : individualEfficiency;

  const displayData = useMemo((): DisplayData | null => {
    // No indicator for skipped tasks
    if (isSkipped) {
      return null;
    }

    // No efficiency data means not started
    if (!efficiency) {
      return null;
    }

    const { startedAt, firstCompletedAt, reopenCount } = efficiency;

    // Completed state
    if (firstCompletedAt) {
      const start = new Date(startedAt);
      const end = new Date(firstCompletedAt);
      const milliseconds = Math.max(0, end.getTime() - start.getTime());
      const formatted = format.duration.formatDuration(milliseconds);
      const suffix = reopenCount > 0 ? ` (reopened ${reopenCount}x)` : undefined;
      return {
        formatted,
        label: 'Completed:',
        milliseconds,
        suffix,
      };
    }

    // In progress state
    const start = new Date(startedAt);
    const milliseconds = Math.max(0, Date.now() - start.getTime());
    const formatted = format.duration.formatDuration(milliseconds);
    return {
      formatted,
      label: 'In progress:',
      milliseconds,
    };
  }, [efficiency, isSkipped]);

  if (!displayData) {
    return null;
  }

  const { formatted, label, suffix } = displayData;

  return (
    <p className={cn('caption text-muted-foreground', className)}>
      {label} {formatted}
      {suffix}
    </p>
  );
};

export default TaskEfficiencyIndicator;
